import type { AeropayBankAccount } from '@iheartjane/data-access';
import type { MarginProperties } from '@iheartjane/reefer';
import {
  Box,
  ConditionalWrapper,
  Flex,
  JaneLogo,
  JanePayLogo,
  Link,
  Typography,
} from '@iheartjane/reefer';

import styles from './bankAccountDetails.module.css';
import { BankCircledIcon } from './bankIcon';

export interface BankAccountDetailsProps extends MarginProperties {
  /** Bank account passed to the component for displaying its details and selected status if "selected" flag is on */
  bankAccount: AeropayBankAccount;
  /** When true, the Jane Pay logo will be replaced with "Powered by Jane" */
  hideJanePayBranding?: boolean;
  /** When true (along with hideJanePayBranding), the "Powered By Jane" logo will not be rendered */
  hidePoweredByJane?: boolean;
  limitLogoSize?: boolean;
  /** Function passed used for selecting a bank account */
  onSelect?: (bankAccountId: string | undefined) => void;
  /** Flag that indicates if the component should display which bank account is selected, and enable the selection of another one */
  selectable?: boolean;
}

/**
 * Display a user's Aeropay bank account details
 */
export const BankAccountDetails = ({
  bankAccount,
  limitLogoSize = false,
  hideJanePayBranding = false,
  hidePoweredByJane = false,
  onSelect = () => null,
  selectable = false,
  ...marginProps
}: BankAccountDetailsProps) => {
  return (
    <Flex
      alignItems="center"
      {...marginProps}
      width="100%"
      className={styles['container']}
    >
      <BankCircledIcon name={bankAccount.bank_name} />

      <ConditionalWrapper
        condition={bankAccount?.is_selected}
        wrapper={(children) => (
          <div className={styles['selectedWrapper']}>{children}</div>
        )}
      >
        <Box ml={16}>
          {bankAccount.bank_name && (
            <Typography variant="body-bold" maxLines={2}>
              {bankAccount.bank_name}
            </Typography>
          )}

          {bankAccount.name && (
            <Typography variant="body" maxLines={2}>
              {bankAccount.name}
            </Typography>
          )}

          {selectable && (
            <Flex>
              {bankAccount.is_selected ? (
                <Typography variant="body">Primary account</Typography>
              ) : (
                <Link onClick={() => onSelect(bankAccount.bank_account_id)}>
                  Set as primary account
                </Link>
              )}{' '}
            </Flex>
          )}
        </Box>

        {bankAccount?.is_selected && !hideJanePayBranding && (
          <JanePayLogo
            height="24px"
            className={styles['janePayLogo']}
            style={limitLogoSize ? { maxWidth: 95, width: '100%' } : {}}
          />
        )}

        {bankAccount?.is_selected &&
          hideJanePayBranding &&
          !hidePoweredByJane && (
            <>
              <Flex alignItems="center" className={styles['poweredByMobile']}>
                <Typography variant="mini-bold" mr={4} mt={8}>
                  Powered By
                </Typography>

                <JaneLogo
                  removeSafeSpace
                  variant="secondary"
                  className={styles['janeLogo']}
                  size="xs"
                />
              </Flex>
              <Flex alignItems="center" className={styles['poweredByDesktop']}>
                <Typography variant="body" mr={4}>
                  Powered By
                </Typography>

                <JaneLogo
                  removeSafeSpace
                  variant="secondary"
                  className={styles['janeLogo']}
                  size="sm"
                />
              </Flex>
            </>
          )}
      </ConditionalWrapper>
    </Flex>
  );
};
